// ============================================================================
// Hooks — registre audit.PiiAccessLog (JOURNAL-LOI25)
// ============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, buildQueryString } from '@gs/api-client';
import { ADMIN_ROOT_KEY } from './useAdminAudit';

export interface PiiAccessLogEntry {
  id: string;
  accessedAt: string;
  actorEmail: string | null;
  fieldName: string;
  recordType: string;
  recordId: string;
  subjectLabel: string;
  reason: string | null;
  ipAddress: string | null;
}

export interface PiiAccessLogFiltersState {
  from?: string;
  to?: string;
  actorEmail?: string;
  subjectQuery?: string;
  fieldName?: string;
  recordType?: string;
}

export interface PiiAccessLogResponse {
  viewer: { email: string; orgLevel: 'gestionnaire' | null };
  retentionYears: number;
  retentionMinYears: number;
  items: PiiAccessLogEntry[];
  total: number;
  page: number;
  pageSize: number;
}

const piiLogKey = [...ADMIN_ROOT_KEY, 'pii-access-log'] as const;

function filtersToParams(filters: PiiAccessLogFiltersState, page: number, pageSize: number): string {
  return buildQueryString({
    page,
    pageSize,
    from: filters.from || undefined,
    to: filters.to || undefined,
    actorEmail: filters.actorEmail || undefined,
    subjectQuery: filters.subjectQuery || undefined,
    fieldName: filters.fieldName || undefined,
    recordType: filters.recordType || undefined,
  });
}

export function usePiiAccessLog(
  enabled: boolean,
  filters: PiiAccessLogFiltersState,
  page: number,
  pageSize = 50,
) {
  const qs = filtersToParams(filters, page, pageSize);
  return useQuery({
    queryKey: [...piiLogKey, filters, page, pageSize],
    queryFn: () => api.get<PiiAccessLogResponse>(`/api/admin/pii-access-log?${qs}`),
    enabled,
    retry: false,
  });
}

export function usePiiAccessLogRetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (years: number) =>
      api.put<{ ok: boolean; years: number; minYears: number }>('/api/admin/pii-access-log/retention', { years }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: piiLogKey });
    },
  });
}

/** Telecharge un export binaire (CSV/PDF) avec les filtres actifs. */
export async function downloadPiiAccessLogExport(
  kind: 'csv' | 'pdf',
  filters: PiiAccessLogFiltersState,
  getAccessToken: () => Promise<string | null>,
): Promise<void> {
  const qs = buildQueryString({
    from: filters.from || undefined,
    to: filters.to || undefined,
    actorEmail: filters.actorEmail || undefined,
    subjectQuery: filters.subjectQuery || undefined,
    fieldName: filters.fieldName || undefined,
    recordType: filters.recordType || undefined,
  });
  const token = await getAccessToken();
  const path = kind === 'csv' ? '/api/admin/pii-access-log/export.csv' : '/api/admin/pii-access-log/export.pdf';
  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}${path}?${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`export-${kind}-failed`);
  const blob = await res.blob();
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const ext = kind === 'csv' ? 'csv' : 'pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `registre-pii-${stamp}.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}
